import type { UndefinedOnPartialDeep } from 'type-fest';

import type { AssetDataType, AssetDrawing } from '@repo/types/asset';
import type { Drawing, Drawings } from '@repo/types/drawing';

import { getAssetData } from './customer-assets.selectors';

const selectDrawingsFn = getAssetData(
    (data) => data.assetDetails._embedded?.planners,
    (data) => data.assetDetails.cartData?.drawings,
    (data) => data.assetDetails.cartData?.drawings
);

export const selectDrawings = (data?: AssetDataType) => {
    const drawings = selectDrawingsFn(data);
    if (!drawings) {
        return;
    }

    const drawingsData = normalizeData(drawings);
    return drawingsData;
};

/**
 * Normalizes the given drawings data by filtering out duplicate entries based on `plannerId` or `drawingId`.
 *
 * @param drawings - An array of drawing objects to be normalized.
 * @returns An array of unique `AssetDrawing` objects.
 *
 * Each drawing object in the returned array will have the following properties:
 * - `drawingName`: The name of the drawing, derived from `drawingName` or `plannerId` of the input item.
 * - `drawingId`: The ID of the drawing, derived from `drawingId` or `plannerId` of the input item.
 * - `drawingIdSource`: The source of the drawing ID, derived from `drawingIdSource` or `plannerIdSource` of the input item.
 * - `drawingUrl`: The URL of the drawing.
 * - `drawingUrlType`: The type of the drawing URL.
 * - `imageUrl`: The URL of the image associated with the drawing.
 */
const normalizeData = (drawings: Drawings): AssetDrawing[] => {
    const uniqueDrawingsMap = new Map<string, AssetDrawing>();

    for (const item of drawings) {
        const drawingId = getDrawingId(item);

        if (drawingId && !uniqueDrawingsMap.has(drawingId)) {
            uniqueDrawingsMap.set(drawingId, {
                drawingName: getDrawingName(item),
                drawingId: drawingId,
                drawingIdSource: getDrawingIdSource(item),
                drawingUrl: item.drawingUrl,
                drawingUrlType: item.drawingUrlType,
                imageUrl: item.imageUrl,
            });
        }
    }

    return Array.from(uniqueDrawingsMap.values());
};

const getDrawingName = (item: UndefinedOnPartialDeep<Drawing>): string | undefined => {
    return 'drawingName' in item ? item.drawingName : 'plannerId' in item ? item.plannerId : undefined;
};

const getDrawingId = (item: UndefinedOnPartialDeep<Drawing>): string | undefined => {
    return 'drawingId' in item ? item.drawingId : 'plannerId' in item ? item.plannerId : undefined;
};

const getDrawingIdSource = (item: UndefinedOnPartialDeep<Drawing>): string | undefined => {
    return 'drawingIdSource' in item
        ? item.drawingIdSource
        : 'plannerIdSource' in item
          ? item.plannerIdSource
          : undefined;
};
